import React, { useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { pageSamples } from '../data/content';
import { WorksheetPreview } from './worksheet/WorksheetPreview';
export function InsidePages() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState<number>(0);
  const total = pageSamples.length;
  const goTo = (next: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(total - 1, next));
    track.scrollTo({
      left: clamped * track.clientWidth,
      behavior: 'smooth'
    });
    setIndex(clamped);
  };
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    const current = Math.round(track.scrollLeft / track.clientWidth);
    if (current !== index) setIndex(current);
  };
  return <section id="inside-pages" className="w-full bg-navy px-5 py-20 text-white sm:py-28" data-id="element-154" data-mp="3pnzqnb8oachz">
      <div className="mx-auto max-w-6xl" data-id="element-155" data-mp="3ktdcj0b7i5u7">
        <div className="mx-auto max-w-2xl text-center" data-id="element-156" data-mp="1mm2szs0fyrc6">
          <p className="font-display text-sm font-bold text-sun" data-id="element-157" data-mp="1moficq8j43z9">Blättere durch das Material</p>
          <h2 className="mt-4 font-display text-[1.9rem] font-extrabold leading-tight tracking-tight sm:text-[2.8rem]" data-id="element-158" data-mp="1rnjqrenmpxoj">
            Sieh dir die Seiten von innen an.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-white/70" data-id="element-159" data-mp="1hu2w68fo4ejt">
            Echte Seiten aus dem Paket – von den ersten Grundlagen bis zu Algebra, Geometrie,
            Statistik und Problemlösen.
          </p>
        </div>

        <div className="relative mt-12" data-id="element-160" data-mp="uv6gwtm7gqpy">
          <div ref={trackRef} onScroll={handleScroll} role="group" aria-roledescription="Karussell" aria-label="Beispielseiten aus dem Material" tabIndex={0} onKeyDown={event => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            goTo(index + 1);
          }
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goTo(index - 1);
          }
        }} className="no-scrollbar flex w-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain rounded-3xl focus:outline-none focus-visible:ring-4 focus-visible:ring-sun/40" data-id="element-161" data-mp="uw839u8o8zmz">
            {pageSamples.map((page, pageIndex) => <article key={page.title} aria-roledescription="Folie" aria-label={`${pageIndex + 1} von ${total}`} className="w-full min-w-0 shrink-0 snap-start" data-id="element-162" data-mp="ors5qwlewdvi" data-mpi={pageIndex}>
                <div className="grid gap-8 rounded-3xl bg-navy-soft p-5 sm:p-8 md:grid-cols-2 md:items-center md:gap-12 md:p-12" data-id="element-163" data-mp="fy6nh3a1gehc">
                  <WorksheetPreview band={page.band} level={page.level} title={page.title} visual={page.visual} header={page.header} data-id="element-164" data-mp="18hcbfy67d5zb" />
                  <div className="min-w-0" data-id="element-165" data-mp="1b33b3ysvalfb">
                    <span className="inline-block rounded-full bg-sun px-3 py-1 text-xs font-bold text-navy" data-id="element-166" data-mp="1fztogedaf2x1">
                      {page.level}
                    </span>
                    <h3 className="mt-4 font-display text-2xl font-extrabold leading-snug sm:text-3xl" data-id="element-167" data-mp="1l1sqnkxajtse">
                      {page.title}
                    </h3>
                    <p className="mt-3 text-base leading-relaxed text-white/75" data-id="element-168" data-mp="1tzl7itinv9hw">{page.caption}</p>
                    <p className="mt-6 font-display text-sm font-bold tabular-nums text-white/50" data-id="element-169" data-mp="1yxmq7dc6d6su">
                      {pageIndex + 1} / {total}
                    </p>
                  </div>
                </div>
              </article>)}
          </div>

          <div className="mt-6 flex items-center justify-center gap-4" data-id="element-170" data-mp="2crdp7rsa58ab">
            <button type="button" onClick={() => goTo(index - 1)} disabled={index === 0} aria-label="Vorherige Seite" className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-navy transition-[transform,opacity] duration-150 ease-out hover:scale-105 disabled:opacity-40 disabled:hover:scale-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-sun/50" data-id="element-171" data-mp="2hpp0m6y83qun">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-172" data-mp="28vrrk37rt1yc" />
            </button>
            <div className="flex items-center gap-1.5" aria-hidden="true" data-id="element-173" data-mp="1l4dyfv1sp2cb">
              {pageSamples.map((page, dotIndex) => <span key={page.title} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${dotIndex === index ? 'w-6 bg-sun' : 'w-2 bg-white/30'}`} data-id="element-174" data-mp="1v2a96lqtlv9w" data-mpi={dotIndex} />)}
            </div>
            <button type="button" onClick={() => goTo(index + 1)} disabled={index === total - 1} aria-label="Nächste Seite" className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-navy transition-[transform,opacity] duration-150 ease-out hover:scale-105 disabled:opacity-40 disabled:hover:scale-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-sun/50" data-id="element-175" data-mp="31ivpjo8g4g0m">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-176" data-mp="16ykccqhb9g12" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}